import React, { useState, useEffect } from 'react';
import LayoutNavSectionList from 'components/layout_and_quote_builder/LayoutNavSectionList.jsx';
import LayoutContext from 'components/contexts/LayoutContext.jsx';
import QuoteBuilderManagePanel from 'components/layout_and_quote_builder/quote_builder/QuoteBuilderManagePanel.jsx';
import QuoteBuilderForm from 'components/layout_and_quote_builder/quote_builder/QuoteBuilderForm.jsx';
import PresentedDielineOptions from 'components/layout_and_quote_builder/quote_builder/PresentedDielineOptions.jsx';
import styles from 'components/layout_and_quote_builder/_style.module.scss';
import MyEventListener from 'components/common/MyEventListener.jsx';
import { CacheProvider } from 'components/layout_and_quote_builder/quote_builder/CacheContext.jsx';
import ErrorBoundary from 'components/common/ErrorBoundary.jsx';
import { readSelectedManufacturingLineList } from 'components/frank/utils/manufacturingLineListUrlState.js';
import { DebugPopupProvider } from 'components/layout_and_quote_builder/DebugPopup.jsx';

// Frank's tabbed front end for the Manufacturing Cost Calculator, served at /mcc. It reuses the same content
// components the legacy stacked page (ShowLayoutAndQuoteBuilderPage) renders -- they already save to the
// user_quote, handle read-only, currency and status -- but lays them out as tabs instead of one long page:
//   Manage (quantity / windows / currency + status) | one tab per nav-builder section (e.g. Carton) |
//   Customer (name / email / phone / notes) | Dieline (material + dimensions).
// The section tabs render LayoutNavSectionList with a single section, so the existing Frank-mode gating
// (fetch Frank's section-material-sheets endpoint, hide the manufacturing lines + solutions) carries over.
const FrankQuoteBuilderPage = ({
  config, navSections, dimensionGroups, layoutOptions, formulaOptions, optionsUserEntered, unit, locale,
  theme, btn_action, dimension_texts_on_layouts_enabled, user_quote_id, dieline_svg
}) => {
  const LayoutContextValues = {
    dimensionGroups, layoutOptions, formulaOptions, optionsUserEntered, unit, locale, theme, btn_action,
    dimension_texts_on_layouts_enabled,
  };

  // Manage, then one tab per nav-builder part section (e.g. Carton), then Customer and Dieline. The
  // 'general' section is the nav builder's catch-all, not a manufacturing part_type, so it is not a tab
  // (keep the original index for the single-section list below).
  const sectionTabs = navSections
    .map((section, index) => ({ section, index }))
    .filter(({ section }) => section.part_type !== 'general')
    .map(({ section, index }) => ({
      key: `section_${index}`,
      label: section.label || section.part_type,
      sectionIndex: index,
    }));
  const tabs = [
    { key: 'manage', label: 'Manage' },
    ...sectionTabs,
    { key: 'customer', label: 'Customer' },
    { key: 'dieline', label: t('quote_builder.dieline') },
  ];

  const [count, setCount] = useState(1000);
  // Open on the section a refresh / shared link picked a plan in (its manufacturing_line_<index> is in the
  // URL), so the restored selection reopens on its own section. Otherwise the first part section (e.g. Carton)
  // -- the manufacturing costs it lists are what the page is FOR, so it is what a user wants to see first. A
  // quote with no part section (only the nav builder's 'general' catch-all) has no section tab, and falls back
  // to Manage.
  const restoredSectionTab = sectionTabs.find(
    (tab) => readSelectedManufacturingLineList(tab.sectionIndex) !== null
  );
  const [activeTab, setActiveTab] = useState(restoredSectionTab?.key || sectionTabs[0]?.key || 'manage');

  useEffect(() => { window.user_quote_id = user_quote_id; }, []);

  MyEventListener([
    { key: 'quote_builder.count_changed', callback: (msg) => { setCount(() => msg.detail); } }
  ]);

  const readOnly = window.user_quote_finalised;

  const isSectionTab = sectionTabs.some((tab) => tab.key === activeTab);

  const renderActiveTab = () => {
    if (activeTab === 'manage') {
      return (
        <>
          <QuoteBuilderForm config={config.general} readOnly={readOnly} />
          <QuoteBuilderManagePanel readOnly={readOnly} />
        </>
      );
    }
    if (activeTab === 'customer') {
      return <QuoteBuilderForm config={config.customer} readOnly={readOnly} />;
    }
    if (activeTab === 'dieline') {
      // Show the dieline SVG (rendered server-side with its dimension texts) above the material +
      // dimension table, so the user can see the dieline being priced. dieline_svg is inline SVG markup.
      return (
        <>
          {dieline_svg && (
            <div
              style={{ maxWidth: '930px', margin: '0 auto 15px' }}
              dangerouslySetInnerHTML={{ __html: dieline_svg }}
            />
          )}
          <div style={{ maxWidth: '620px', margin: '0 auto' }}>
            <PresentedDielineOptions />
          </div>
        </>
      );
    }

    const sectionTab = sectionTabs.find((tab) => tab.key === activeTab);
    if (sectionTab) {
      // A single-section list, so the section's own Frank-mode material-sheet fetch + gating are reused.
      return <LayoutNavSectionList navSections={[navSections[sectionTab.sectionIndex]]} count={count} config={config} />;
    }
    return null;
  };

  const tabButtonStyle = (isActive) => ({
    padding: '10px 22px',
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: isActive ? 'bold' : 'normal',
    color: isActive ? '#eb5424' : '#555',
    borderBottom: isActive ? '3px solid #eb5424' : '3px solid transparent',
    marginBottom: '-2px',
  });

  return (
    <ErrorBoundary componentName="FrankQuoteBuilderPage" errorContext={{ user_quote_id: user_quote_id }}>
      <CacheProvider initialCache={window.user_quote_section_properties_cached}>
        <LayoutContext.Provider value={LayoutContextValues}>
          <DebugPopupProvider>
          <div className={`${styles.component_container} container`}>
            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', borderBottom: '2px solid #ddd', marginBottom: '25px' }}>
              {tabs.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setActiveTab(tab.key)}
                  style={tabButtonStyle(activeTab === tab.key)}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* A FORM tab is constrained to a readable column, centered, so its labels sit near their inputs
                rather than stretching label-left / input-right across the whole page. A SECTION tab is not:
                it lists the manufacturing line cards, which are sized as a fraction of this container (4 to a
                row, like the legacy listing) -- in a 620px column each card is ~139px and its machine columns
                are crushed to ~60px. So the section tabs get the container's full width. */}
            <div style={isSectionTab || activeTab === 'dieline' ? undefined : { maxWidth: '620px', margin: '0 auto' }}>
              {renderActiveTab()}
            </div>
          </div>
          </DebugPopupProvider>
        </LayoutContext.Provider>
      </CacheProvider>
    </ErrorBoundary>
  );
};

export default FrankQuoteBuilderPage;
